import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { ThemeProvider, studioTheme } from '@sanity/ui';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import * as Sentry from '@sentry/react';

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  environment: process.env.NODE_ENV,
  integrations: [Sentry.browserTracingIntegration()],
  tracesSampleRate: 0.1,
  // Only send errors in production to avoid noise during development
  enabled: process.env.NODE_ENV === 'production',
  // Errors thrown by scripts injected by in-app browsers (Instagram, Facebook,
  // TikTok, etc.) rather than our own code — these come from the host app's
  // native bridge, not anything we control, so they're not actionable here.
  ignoreErrors: [
    /window\.webkit\.messageHandlers/,
    'sendDataToNative',
    'sendPageHideMessage',
    // styled-components error #17: its managed <style> tag was unmounted or
    // altered by a third party. Seen coming from a browser extension
    // ("obscura:bootstrap") injecting into the page — not our bug.
    /styled-components.*errors\.md#17/,
    // Classic third-party/extension noise that shows up across most sites
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications',
    'Script error.',
  ],
  // Drop events whose top frame comes from a browser extension's own
  // injected context rather than our bundle.
  denyUrls: [/^(chrome|moz|safari-web)-extension:\/\//i, /obscura/i],
});

// Unregister any stale service workers from previous builds.
// A leftover SW from older builds can serve stale assets.
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    registrations.forEach((reg) => reg.unregister());
  });
}

// Enhanced Error Boundary with hydration error support
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      isHydrationError: false
    };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error,
      isHydrationError: error.message.includes('hydration')
    };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error caught by ErrorBoundary:", error, errorInfo);
    Sentry.captureException(error, { contexts: { react: errorInfo } });
  }

  handleFullReload = () => {
    // Clear cache and reload for hydration errors
    if (this.state.isHydrationError) {
      if (window.caches) {
        caches.keys().then(names => {
          names.forEach(name => caches.delete(name));
        });
      }
    }
    window.location.reload(true);
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          padding: '2rem',
          textAlign: 'center',
          maxWidth: '800px',
          margin: '0 auto'
        }}>
          <h2>Application Error</h2>
          <p style={{ color: '#e03131', margin: '1rem 0' }}>
            {this.state.error?.message || 'Unknown error occurred'}
          </p>
          {this.state.isHydrationError && (
            <p style={{ margin: '1rem 0' }}>
              This appears to be a rendering mismatch. A full refresh may help.
            </p>
          )}
          <button
            onClick={this.handleFullReload}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#4263eb',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '1rem'
            }}
          >
            {this.state.isHydrationError ? 'Clear Cache & Reload' : 'Refresh Page'}
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Safe root element initialization with hydration error prevention
function initializeApp() {
  // Create a clean root container if needed
  const rootElement = document.getElementById('root');

  if (!rootElement) {
    const newRoot = document.createElement('div');
    newRoot.id = 'root';
    document.body.appendChild(newRoot);
    return newRoot;
  }

  // Clear any existing content that might cause hydration mismatch
  if (rootElement.innerHTML.trim() !== '') {
    console.warn('Existing content in root element - clearing to prevent hydration issues');
    rootElement.innerHTML = '';
  }

  return rootElement;
}

// Main render function with error protection
function renderApp() {
  try {
    const rootElement = initializeApp();
    const root = ReactDOM.createRoot(rootElement);

    root.render(
      <React.StrictMode>
        <ErrorBoundary>
          <HelmetProvider>
            <BrowserRouter>
              <ThemeProvider theme={studioTheme}>
                <App />
              </ThemeProvider>
            </BrowserRouter>
          </HelmetProvider>
        </ErrorBoundary>
      </React.StrictMode>
    );

    return root;
  } catch (error) {
    console.error('Failed to render application:', error);

    // Fallback error display if rendering fails completely
    // NOTE: Uses safe DOM API (textContent) instead of innerHTML to prevent XSS
    const rootElement = document.getElementById('root') || document.body;
    const wrapper = document.createElement('div');
    wrapper.setAttribute('style', 'padding: 2rem; text-align: center; font-family: sans-serif;');

    const heading = document.createElement('h2');
    heading.setAttribute('style', 'color: #e03131;');
    heading.textContent = 'Critical Rendering Error';

    const msg1 = document.createElement('p');
    msg1.textContent = 'Failed to initialize the application.';

    const msg2 = document.createElement('p');
    msg2.textContent = error.message; // textContent is safe — no XSS

    const btn = document.createElement('button');
    btn.setAttribute('style', 'padding: 0.75rem 1.5rem; background: #228be6; color: white; border: none; border-radius: 4px; cursor: pointer; margin-top: 1rem;');
    btn.textContent = 'Force Reload';
    btn.onclick = () => window.location.reload(true);

    wrapper.appendChild(heading);
    wrapper.appendChild(msg1);
    wrapper.appendChild(msg2);
    wrapper.appendChild(btn);
    rootElement.innerHTML = '';
    rootElement.appendChild(wrapper);
  }
}

// Initialize the application
renderApp();

// Web Vitals reporting
if (process.env.NODE_ENV === 'production') {
  reportWebVitals();
}